import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiFetchJson } from '../../apiClient';

function ChoreSettings({ instance, categories, deadlinePresets, onCategoriesChanged, onPresetsChanged, showMessage }) {
  return (
    <div className="chore-settings">
      <CategoryManager
        instance={instance}
        categories={categories}
        onChanged={onCategoriesChanged}
        showMessage={showMessage}
      />
      <DeadlinePresetManager
        instance={instance}
        presets={deadlinePresets}
        onChanged={onPresetsChanged}
        showMessage={showMessage}
      />
    </div>
  );
}

function CategoryManager({ instance, categories, onChanged, showMessage }) {
  const { t } = useTranslation();
  const [showForm, setShowForm] = useState(false);
  const [nameEn, setNameEn] = useState('');
  const [nameNo, setNameNo] = useState('');
  const [icon, setIcon] = useState('📌');

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await apiFetchJson(instance, '/api/chore-categories', {
        method: 'POST',
        body: JSON.stringify({ nameEn, nameNo, icon })
      });
      onChanged();
      setShowForm(false);
      setNameEn(''); setNameNo(''); setIcon('📌');
      showMessage(t('chores.messages.categoryCreated'));
    } catch (e) {
      console.error('Failed to create category:', e);
      showMessage(e.message || 'Failed to create category', 'error');
    }
  };

  const handleDelete = async (id) => {
    try {
      await apiFetchJson(instance, `/api/chore-categories/${id}`, { method: 'DELETE' });
      onChanged(); showMessage(t('chores.messages.categoryDeleted'));
    } catch (e) {
      console.error('Failed to delete category:', e);
      showMessage(e.message || t('chores.messages.cannotDeleteCategory'), 'error');
    }
  };

  return (
    <div className="chore-settings-section">
      <div className="chore-settings-header">
        <h3>📂 {t('chores.settings.choreCategories')}</h3>
        <button className="chore-btn chore-btn-primary chore-btn-small" onClick={() => setShowForm(!showForm)}>
          {showForm ? t('chores.settings.cancel') : t('chores.settings.add')}
        </button>
      </div>

      {showForm && (
        <form className="chore-settings-form" onSubmit={handleCreate}>
          <input type="text" value={icon} onChange={(e) => setIcon(e.target.value)} placeholder={t('chores.settings.iconPlaceholder')} className="chore-input chore-input-icon" maxLength={4} />
          <input type="text" value={nameEn} onChange={(e) => setNameEn(e.target.value)} placeholder={t('chores.settings.englishName')} className="chore-input" required />
          <input type="text" value={nameNo} onChange={(e) => setNameNo(e.target.value)} placeholder={t('chores.settings.norwegianName')} className="chore-input" required />
          <button type="submit" className="chore-btn chore-btn-primary chore-btn-small">{t('chores.settings.create')}</button>
        </form>
      )}

      <div className="chore-settings-list">
        {categories.map(cat => (
          <div key={cat.id} className="chore-settings-item">
            <span>{cat.icon} {cat.nameEn} / {cat.nameNo}</span>
            {cat.isSystemDefault
              ? <span className="chore-badge">{t('chores.settings.system')}</span>
              : <button className="chore-btn chore-btn-danger chore-btn-tiny" onClick={() => handleDelete(cat.id)}>🗑</button>
            }
          </div>
        ))}
      </div>
    </div>
  );
}

function DeadlinePresetManager({ instance, presets, onChanged, showMessage }) {
  const { t } = useTranslation();
  const presetTypeLabels = {
    0: t('chores.settings.presetTypes.0'), 1: t('chores.settings.presetTypes.1'),
    2: t('chores.settings.presetTypes.2'), 3: t('chores.settings.presetTypes.3'),
    4: t('chores.settings.presetTypes.4'), 5: t('chores.settings.presetTypes.5'),
    6: t('chores.settings.presetTypes.6')
  };
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState('');
  const [presetType, setPresetType] = useState(4); // FixedAnnual default
  const [fixedMonth, setFixedMonth] = useState('');
  const [fixedDay, setFixedDay] = useState('');

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await apiFetchJson(instance, '/api/deadline-presets', {
        method: 'POST',
        body: JSON.stringify({
          name,
          presetType,
          fixedMonth: fixedMonth ? parseInt(fixedMonth) : null,
          fixedDay: fixedDay ? parseInt(fixedDay) : null
        })
      });
      onChanged();
      setShowForm(false);
      setName(''); setFixedMonth(''); setFixedDay('');
      showMessage(t('chores.messages.presetCreated'));
    } catch (e) {
      console.error('Failed to create preset:', e);
      showMessage(e.message || 'Failed to create preset', 'error');
    }
  };

  const handleDelete = async (id) => {
    try {
      await apiFetchJson(instance, `/api/deadline-presets/${id}`, { method: 'DELETE' });
      onChanged(); showMessage(t('chores.messages.presetDeleted'));
    } catch (e) {
      console.error('Failed to delete preset:', e);
      showMessage(e.message || t('chores.messages.cannotDeletePreset'), 'error');
    }
  };

  return (
    <div className="chore-settings-section">
      <div className="chore-settings-header">
        <h3>📅 {t('chores.settings.deadlinePresets')}</h3>
        <button className="chore-btn chore-btn-primary chore-btn-small" onClick={() => setShowForm(!showForm)}>
          {showForm ? t('chores.settings.cancel') : t('chores.settings.add')}
        </button>
      </div>

      {showForm && (
        <form className="chore-settings-form" onSubmit={handleCreate}>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder={t('chores.settings.presetPlaceholder')} className="chore-input" required />
          <select value={presetType} onChange={(e) => setPresetType(parseInt(e.target.value))} className="chore-select">
            {Object.entries(presetTypeLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          {(presetType === 4 || presetType === 5) && (
            <>
              <input type="number" value={fixedMonth} onChange={(e) => setFixedMonth(e.target.value)} placeholder={t('chores.settings.monthPlaceholder')} className="chore-input" min={1} max={12} />
              <input type="number" value={fixedDay} onChange={(e) => setFixedDay(e.target.value)} placeholder={t('chores.settings.dayPlaceholder')} className="chore-input" min={1} max={31} />
            </>
          )}
          <button type="submit" className="chore-btn chore-btn-primary chore-btn-small" disabled={!name.trim()}>{t('chores.settings.create')}</button>
        </form>
      )}

      <div className="chore-settings-list">
        {presets.map(preset => (
          <div key={preset.id} className="chore-settings-item">
            <span>{preset.name}</span>
            <span className="chore-settings-item-type">{presetTypeLabels[preset.presetType]}</span>
            {preset.isSystemDefault
              ? <span className="chore-badge">{t('chores.settings.system')}</span>
              : <button className="chore-btn chore-btn-danger chore-btn-tiny" onClick={() => handleDelete(preset.id)}>🗑</button>
            }
          </div>
        ))}
      </div>
    </div>
  );
}

export default ChoreSettings;
